import { Vector2 } from "../../../common/vector2.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { Easing } from "../../../transforms/transform_base.js";
import { TransformOpacity } from "../../../transforms/transform_opacity.js";
import { TransformRotate } from "../../../transforms/transform_rotate.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { TransformCircularMove } from "../../../transforms/transform_circular_move.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { Transformable } from "../../../transforms/transformable.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { AbstractOverlay } from "../abstract_overlay.js";
import { OverlayId } from "../overlay_ids.js";
import { OverlayManager } from "../overlay_manager.js";

export class AdventureInfoOverlay extends AbstractOverlay
{
  private static readonly TITLE_GRADIENT_TOP = "#FBF2A7";
  private static readonly TITLE_GRADIENT_BOTTOM = "#B84C42";
  private static readonly STAGGER_MS = 56;
  private static readonly BASE_DELAY_MS = 160;
  private static readonly ITEM_ANIM_DURATION_MS = 256;
  private static readonly ARROW_ANIM_DURATION_MS = 280;
  private static readonly TITLE_ANIM_DURATION_MS = 320;

  private content_holder: UIElementHolder | null = null;

  constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    params: any,
    overlay_id: OverlayId
  )
  {
    super( ctx, x, y, width, height, overlay_id, params, false );
    this.createElements();
  }

  private createElements(): void
  {
    const base_now = performance.now() + AdventureInfoOverlay.BASE_DELAY_MS;
    const stagger = AdventureInfoOverlay.STAGGER_MS;

    const addAppearTransform = ( el: Transformable, start_time: number ): void =>
    {
      el.setOpacity( 0 );
      el.setScale( new Vector2( 0.6, 0.6 ) );
      el.addTransform( new TransformOpacity( el, true, 0, 1, start_time, AdventureInfoOverlay.ITEM_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
      el.addTransform( new TransformScale( el, true, null, new Vector2( 1, 1 ), start_time, AdventureInfoOverlay.ITEM_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    };

    this.createCenteredHolder();
    this.createArrowTop( base_now + stagger * 4 );
    this.createArrowBottom( base_now + stagger * 1 );
    this.createTopPart( base_now, stagger, addAppearTransform );
    this.createCenterPart( base_now, stagger, addAppearTransform );
    this.createBottomPart( base_now, stagger, addAppearTransform );
    this.createTitle();
    this.createCloseIcon( base_now + stagger * 9 );
  }

  private static arcParams(
    from: Vector2,
    to: Vector2,
    bulge: number
  ): { center: Vector2; radius: number; start_angle: number; end_angle: number }
  {
    const mid     = new Vector2( ( from.x + to.x ) / 2, ( from.y + to.y ) / 2 );
    const dx      = to.x - from.x;
    const dy      = to.y - from.y;
    const len     = Math.sqrt( dx * dx + dy * dy );
    const perp_x  = -dy / len;
    const perp_y  =  dx / len;
    const center  = new Vector2( mid.x + perp_x * bulge, mid.y + perp_y * bulge );
    const radius      = Math.sqrt( ( from.x - center.x ) ** 2 + ( from.y - center.y ) ** 2 );
    const start_angle = Math.atan2( from.y - center.y, from.x - center.x );
    let   diff        = Math.atan2( to.y   - center.y, to.x   - center.x ) - start_angle;
    while ( diff >  Math.PI ) diff -= 2 * Math.PI;
    while ( diff < -Math.PI ) diff += 2 * Math.PI;
    const end_angle = start_angle + diff;
    return { center, radius, start_angle, end_angle };
  }

  private createCenteredHolder(): void
  {
    const holder_w_design = 982;
    const holder_h_design = 1242;

    const holder_w = LayoutScaler.widthFromDesign( holder_w_design );
    const holder_h = LayoutScaler.heightFromDesign( holder_h_design );
    const holder_x = this.rect.getW2() - holder_w / 2;
    const holder_y = this.rect.getH2() - holder_h / 2;

    this.content_holder = new UIElementHolder(
      this.ctx,
      holder_x,
      holder_y,
      holder_w,
      holder_h
    );

    this.addChild( this.content_holder );
  }

  private createTopPart( base_now: number, stagger: number, addAppearTransform: ( el: Transformable, start_time: number ) => void ): void
  {
    if ( !this.content_holder ) return;

    const top_part_w_design = 304;
    const top_part_h_design = 223;
    const top_part_x_design = 630;

    const top_part_w = LayoutScaler.widthFromDesign( top_part_w_design );
    const top_part_h = LayoutScaler.heightFromDesign( top_part_h_design );
    const top_part_x = LayoutScaler.xFromDesign( top_part_x_design );
    const top_part_y = 0;

    const top_part = new UIImage(
      this.ctx,
      top_part_x,
      top_part_y,
      top_part_w,
      top_part_h,
      ImgID.UI_AdventureInfoTopPart
    );
    addAppearTransform( top_part, base_now );

    const top_part_text_w = LayoutScaler.widthFromDesign( 545 );
    const top_part_text_h = top_part_h;
    const top_part_text_x = LayoutScaler.xFromDesign( 0 );
    const top_part_text_y = 0;

    const top_part_text = new UITextElement(
      this.ctx,
      top_part_text_x,
      top_part_text_y,
      top_part_text_w,
      top_part_text_h,
      {
        localization_key: "ADVENTURE.TOP_TEXT",
        text_color: ColorScheme.white,
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Right,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true,
        fit_font_size: true,
        line_height: 1.17,
        ...TextStyleScheme.buttonShadow(),
      }
    );
    addAppearTransform( top_part_text, base_now + stagger );
    this.content_holder.addChild( top_part_text );
    this.content_holder.addChild( top_part );
  }

  private createCenterPart( base_now: number, stagger: number, addAppearTransform: ( el: Transformable, start_time: number ) => void ): void
  {
    if ( !this.content_holder ) return;

    const center_part_y_design = 160;
    const center_part_w_design = 453;
    const center_part_h_design = 464;

    const center_part_w = LayoutScaler.widthFromDesign( center_part_w_design );
    const center_part_h = LayoutScaler.heightFromDesign( center_part_h_design );
    const center_part_x = LayoutScaler.xFromDesign( 0 );
    const center_part_y = LayoutScaler.yFromDesign( center_part_y_design );

    const center_part = new UIImage(
      this.ctx,
      center_part_x,
      center_part_y,
      center_part_w,
      center_part_h,
      ImgID.UI_AdventureInfoCenterPart,
    );
    addAppearTransform( center_part, base_now + stagger * 3 );
    this.content_holder.addChild( center_part );

    const center_part_text_w = LayoutScaler.widthFromDesign( 531 );
    const center_part_text_h = LayoutScaler.heightFromDesign( 234 );
    const center_part_text_x = LayoutScaler.xFromDesign( 420 );
    const center_part_text_y = center_part_y + LayoutScaler.yFromDesign( 140 );

    const center_part_text = new UITextElement(
      this.ctx,
      center_part_text_x,
      center_part_text_y,
      center_part_text_w,
      center_part_text_h,
      {
        localization_key: "ADVENTURE.CENTER_TEXT",
        text_color: ColorScheme.white,
        font_size: LayoutScaler.textSizeFromDesign( 50 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Left,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true,
        fit_font_size: true,
        line_height: 1.17,
        ...TextStyleScheme.buttonShadow(),
      }
    );
    addAppearTransform( center_part_text, base_now + stagger * 4 );
    this.content_holder.addChild( center_part_text );
  }

  private createBottomPart( base_now: number, stagger: number, addAppearTransform: ( el: Transformable, start_time: number ) => void ): void
  {
    if ( !this.content_holder ) return;

    const bottom_part_w_design = 674;
    const bottom_part_h_design = 341;
    const bottom_part_x_design = 175;
    const bottom_part_y_design = 730;

    const bottom_part_w = LayoutScaler.widthFromDesign( bottom_part_w_design );
    const bottom_part_h = LayoutScaler.heightFromDesign( bottom_part_h_design );
    const bottom_part_x = LayoutScaler.xFromDesign( bottom_part_x_design );
    const bottom_part_y = LayoutScaler.yFromDesign( bottom_part_y_design );

    const bottom_part = new UIImage(
      this.ctx,
      bottom_part_x,
      bottom_part_y,
      bottom_part_w,
      bottom_part_h,
      ImgID.UI_AdventureInfoBottomPart
    );
    addAppearTransform( bottom_part, base_now + stagger * 6 );
    this.content_holder.addChild( bottom_part );

    const bottom_part_text_y = bottom_part_y + bottom_part_h;
    const bottom_part_text_h = LayoutScaler.heightFromDesign( 234 );
    const bottom_part_text_w = LayoutScaler.widthFromDesign( 820 );
    const bottom_part_text_x = this.content_holder.getRect().getW2() - bottom_part_text_w / 2;

    const bottom_part_text = new UITextElement(
      this.ctx,
      bottom_part_text_x,
      bottom_part_text_y,
      bottom_part_text_w,
      bottom_part_text_h,
      {
        localization_key: "ADVENTURE.BOTTOM_TEXT",
        text_color: ColorScheme.white,
        font_size: LayoutScaler.textSizeFromDesign( 38 ),
        min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: true,
        fit_font_size: true,
        line_height: 1.1,
        ...TextStyleScheme.buttonShadow(),
      }
    );
    addAppearTransform( bottom_part_text, base_now + stagger * 7 );
    this.content_holder.addChild( bottom_part_text );
  }

  private createArrowTop( start_time: number ): void
  {
    if ( !this.content_holder ) return;

    // Target: just above bottom_part's top-left (bottom_part sits at
    // x=220,y=730,w=674,h=341 — see createBottomPart). Start: inside
    // center_part (x=0,y=160,w=453,h=464 — see createCenterPart), at the
    // same fractional offset (29.7% across, 46.9% down) this arrow used
    // against LeaderboardInfoOverlay's differently-sized center_part —
    // this class was copy-pasted from there and these four numbers were
    // never re-derived for the adventure art's own (bigger, differently
    // proportioned) part images, so the arrow was swooping through empty
    // space instead of bridging center_part to bottom_part.
    const arrow_x_design = 220;
    const arrow_w_design = 168;
    const arrow_h_design = 155;
    const arrow_y_design = 560;
    const rotation_deg_from = 85;
    const rotation_deg_to = 60;

    const arrow_w = LayoutScaler.widthFromDesign( arrow_w_design );
    const arrow_h = LayoutScaler.heightFromDesign( arrow_h_design );
    const arrow_x = LayoutScaler.xFromDesign( arrow_x_design );
    const arrow_y = LayoutScaler.yFromDesign( arrow_y_design );
    const arrow_start_x = LayoutScaler.widthFromDesign( 135 );
    const arrow_start_y = LayoutScaler.yFromDesign( 378 );

    const from_rad = rotation_deg_from * Math.PI / 180;
    const to_rad = rotation_deg_to * Math.PI / 180;

    const arrow = new UIImage(
      this.ctx,
      arrow_start_x,
      arrow_start_y,
      arrow_w,
      arrow_h,
      ImgID.UI_DailyTasksArrow
    );
    arrow.setOpacity( 0 );
    arrow.setRotation( from_rad );

    const arc_top = AdventureInfoOverlay.arcParams(
      new Vector2( arrow_start_x, arrow_start_y ),
      new Vector2( arrow_x, arrow_y ),
      LayoutScaler.widthFromDesign( -120 )
    );

    arrow.addTransform( new TransformOpacity( arrow, true, 0, 1, start_time, AdventureInfoOverlay.ARROW_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    arrow.addTransform( new TransformRotate( arrow, true, from_rad, to_rad, start_time, AdventureInfoOverlay.ARROW_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    arrow.addTransform( new TransformCircularMove( arrow, true, arc_top.center, arc_top.radius, start_time, AdventureInfoOverlay.ARROW_ANIM_DURATION_MS, arc_top.start_angle, arc_top.end_angle, { easing: Easing.EaseOutQuad } ) );

    this.content_holder.addChild( arrow );
  }

  private createArrowBottom( start_time: number ): void
  {
    if ( !this.content_holder ) return;

    // A tight decorative flourish anchored on top_part itself (the cannon,
    // x=630,y=0,w=304,h=223 — see createTopPart): starts at its center and
    // swoops to a point offset -63.9% of its width / +45.4% of its height
    // from that center, same fractional relationship this arrow used
    // against LeaderboardInfoOverlay's own (taller, narrower) top_part
    // before this class was copy-pasted and re-pointed at adventure's art.
    const arrow_x_design = 578;
    const arrow_w_design = 168;
    const arrow_h_design = 155;
    const arrow_y_design = 223;
    const rotation_deg_from = 182;
    const rotation_deg_to = 137;

    const arrow_w = LayoutScaler.widthFromDesign( arrow_w_design );
    const arrow_h = LayoutScaler.heightFromDesign( arrow_h_design );
    const arrow_x = LayoutScaler.xFromDesign( arrow_x_design );
    const arrow_y = LayoutScaler.yFromDesign( arrow_y_design );
    const arrow_start_x = LayoutScaler.xFromDesign( 782 );
    const arrow_start_y = LayoutScaler.yFromDesign( 112 );

    const from_rad = rotation_deg_from * Math.PI / 180;
    const to_rad = rotation_deg_to * Math.PI / 180;

    const arrow = new UIImage(
      this.ctx,
      arrow_start_x,
      arrow_start_y,
      arrow_w,
      arrow_h,
      ImgID.UI_DailyTasksArrow,
      { direction_x: -1, direction_y: 1 }
    );
    arrow.setOpacity( 0 );
    arrow.setRotation( from_rad );

    const arc_bottom = AdventureInfoOverlay.arcParams(
      new Vector2( arrow_start_x, arrow_start_y ),
      new Vector2( arrow_x, arrow_y ),
      LayoutScaler.widthFromDesign( 120 )
    );

    arrow.addTransform( new TransformOpacity( arrow, true, 0, 1, start_time, AdventureInfoOverlay.ARROW_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    arrow.addTransform( new TransformRotate( arrow, true, from_rad, to_rad, start_time, AdventureInfoOverlay.ARROW_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    arrow.addTransform( new TransformCircularMove( arrow, true, arc_bottom.center, arc_bottom.radius, start_time, AdventureInfoOverlay.ARROW_ANIM_DURATION_MS, arc_bottom.start_angle, arc_bottom.end_angle, { easing: Easing.EaseOutQuad } ) );

    this.content_holder.addChild( arrow );
  }

  private createTitle(): void
  {
    if ( !this.content_holder ) return;

    const title_y_design = 140;

    const title_w = this.rect.getW();
    const title_x = this.rect.getW2() - title_w / 2;
    const title_y = LayoutScaler.yFromDesign( title_y_design );
    const title_offset_y = LayoutScaler.yFromDesign( 20 );
    const now = performance.now();

    const h = LayoutScaler.heightFromDesign( 85 );

    const title = new UITextElement(
      this.ctx,
      0,
      title_w + LayoutScaler.yFromDesign( 2 ),
      title_w,
      h,
      {
        localization_key: "ADVENTURE",
        font_size: LayoutScaler.textSizeFromDesign( 60 ),
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        font_weight: 900,
        // Same lettering as the adventure header title.
        outline_width: LayoutScaler.textBorderSizeFromDesign( 14 ),
        outline_color: ColorScheme.adventure_header_title_outline,
        shadow_blur: LayoutScaler.widthFromDesign( 6 ),
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
        shadow_color: ColorScheme.black_transparent_30,
        auto_wrap: false,
        fit_font_size: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 42 ),
      }
    )

    const title_1 = new UITextElement(
      this.ctx,
      0,
      title_w,
      title_w,
      h,
      {
        localization_key: "ADVENTURE",
        font_size: LayoutScaler.textSizeFromDesign( 60 ),
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        font_weight: 900,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
        shadow_color: ColorScheme.black_transparent
      }
    )

    title.setOpacity( 0 );
    title.setPixelPos( new Vector2( title_x, title_y + title_offset_y ), true );
    title.addTransform( new TransformOpacity( title, true, 0, 1, now, AdventureInfoOverlay.TITLE_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    title.addTransform( new TransformStraightMove( title, true, null, new Vector2( title_x, title_y ), now, AdventureInfoOverlay.TITLE_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );

    title_1.setOpacity( 0 );
    title_1.setPixelPos( new Vector2( title_x, title_y + title_offset_y ), true );
    title_1.addTransform( new TransformOpacity( title_1, true, 0, 1, now, AdventureInfoOverlay.TITLE_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );
    title_1.addTransform( new TransformStraightMove( title_1, true, null, new Vector2( title_x, title_y ), now, AdventureInfoOverlay.TITLE_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );

    this.addChild( title );
    this.addChild( title_1 );
  }

  private createCloseIcon( start_time: number ): void
  {
    const close_icon_w = LayoutScaler.widthFromDesign( 110 );
    const close_icon_h = LayoutScaler.heightFromDesign( 110 );

    const close_icon_x = this.rect.getW2() - close_icon_w / 2;
    const close_icon_y = this.rect.getH() - close_icon_h - LayoutScaler.yFromDesign( 100 );

    const close_icon = new UIImage(
      this.ctx,
      close_icon_x,
      close_icon_y,
      close_icon_w,
      close_icon_h,
      ImgID.UI_DailyTasksCloseBtn,
      {},
      true
    )

    close_icon.setOpacity( 0 );
    close_icon.addTransform( new TransformOpacity( close_icon, true, 0, 1, start_time, AdventureInfoOverlay.ITEM_ANIM_DURATION_MS, { easing: Easing.EaseOutQuad } ) );

    this.addChild( close_icon );
  }

  public getDimOpacity(): number
  {
    return 0.85;
  }

  public onTouchStart( p: Vector2, now: number ): boolean
  {
    super.onTouchStart( p, now );
    OverlayManager.closeOverlay();
    return true;
  }

  public update( now: number ): void
  {
    super.update( now );
  }

  public render(): void
  {
    if ( !this.shouldBeRendered() ) return;

    this.startRender();
    this.finishRender();
  }

  public name(): string
  {
    return "AdventureInfoOverlay";
  }
}
